import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { useDebounce } from 'primereact/hooks';
import { InputText } from 'primereact/inputtext';
import { Paginator } from 'primereact/paginator';
import { Toast } from 'primereact/toast';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import ProgramCreate from '../component/program/create';
import ProgramUpdate from '../component/program/update';
import { subscribe, unsubscribe } from '../context/events';
import { useSite } from '../context/site';
import { MSG, PLeft } from '../shared';

function ProgramsPage() {
    const { GetPrograms } = useSite();

    const toast = useRef(null);
    const initRef = useRef(false);

    const [loading, setLoading] = useState(true);
    const [programs, setPrograms] = useState([]);
    const [limit, setLimit] = useState(5);
    const [skip, setSkip] = useState(0);
    const [sortField, setSortField] = useState('modifiedAt');
    const [sortOrder, setSortOrder] = useState(-1);
    const [filters, setFilters] = useState([]);
    const [sortData, setSortData] = useState({ modifiedAt: -1 });
    const [recordCount, setRecordCount] = useState(0);
    const [filteredRecordCount, setFilteredRecordCount] = useState(0);
    const [openCreate, setOpenCreate] = useState(false);
    const [openUpdate, setOpenUpdate] = useState(false);
    const [updateData, setUpdateData] = useState({});
    const [globalFilterValue, dbGlobalFilterValue, setGlobalFilterValue] = useDebounce('', 420);

    const getItems = useCallback(
        (filters, sortData, skip, limit, reset) => {
            setLoading(true);

            const data = {
                filters: Array.isArray(filters) ? filters : 0,
                skip: skip ? skip : 0,
                limit: limit ? limit : 0,
                sorts: sortData ? sortData : {}
            };

            GetPrograms(data, (res) => {
                if (res.status === 200) {
                    setPrograms(res.data.programs);
                    setRecordCount(res.data.count);
                    setFilteredRecordCount(res.data.filterCount);
                }

                if (!!reset) {
                    setFilters(data.filters);
                    setSortData(data.sorts);
                    setSortField('');
                    setSortOrder(1);
                    setSkip(data.skip);
                    setLimit(data.limit);
                }

                setLoading(false);
            });
        },
        [GetPrograms]
    );

    const processEvent = useCallback(
        (event) => {
            const { detail: res, type: action } = event;

            if (res.status === 200) {
                if (action === MSG.create.program) {
                    toast.current.show({
                        severity: 'success',
                        summary: 'Program Created',
                        detail: `${res.data.program.programName} was created`,
                        life: 3000
                    });
                }

                if (action === MSG.update.program) {
                    toast.current.show({
                        severity: 'success',
                        summary: 'Program Updated',
                        detail: `${res.data.program.programName} was updated`,
                        life: 3000
                    });

                    const programFound = programs.find((p) => p._id === res.data.program._id);

                    if (programFound) {
                        setPrograms((prev) => {
                            return [
                                ...prev.map((p) => {
                                    if (p._id === programFound._id) {
                                        return {
                                            ...res.data.program
                                        };
                                    }

                                    return p;
                                })
                            ];
                        });
                    }
                }
            }
        },
        [programs]
    );

    const ActionsDisplay = (program) => {
        return (
            <>
                <Button
                    icon="pi pi-user-edit"
                    title={`Edit ${program.programName}`}
                    size="small"
                    rounded
                    severity="help"
                    onClick={() => {
                        setUpdateData(program);
                        setOpenUpdate(true);
                    }}
                />

                <Link to={`/program/${program._id}`}>
                    <Button
                        icon="pi pi-user"
                        className="ml-1"
                        size="small"
                        severity="secondary"
                        rounded
                        title="Go to Profile"
                    />
                </Link>
            </>
        );
    };

    const AddressDisplay = (program) => {
        if (!program.siteStreetAddress1) {
            return 'No Street Address';
        }

        let res = program.siteStreetAddress1;

        if (program.siteStreetAddress2) {
            res += ` ${program.siteStreetAddress2}`;
        }

        if (program.siteCity) {
            res += `, ${program.siteCity}`;
        }

        if (program?.siteState?.itemData?.abbr) {
            res += `, ${program.siteState.itemData.abbr}`;
        }

        if (program.siteZipCode) {
            res += ` ${program.siteZipCode}`;
        }

        return res;
    };

    const NotesDisplay = (program) => {
        if (!program.notes) {
            return '';
        }

        return <span dangerouslySetInnerHTML={{ __html: program.notes }}></span>;
    };

    const EmailDisplay = (program) => {
        if (!program.email) {
            return '';
        }

        return <a href={`mailto:${program.email}`}>{program.email}</a>;
    };

    // App Events
    useEffect(() => {
        subscribe(MSG.create.program, processEvent);
        subscribe(MSG.update.program, processEvent);

        return () => {
            unsubscribe(MSG.create.program, processEvent);
            unsubscribe(MSG.update.program, processEvent);
        };
    }, [processEvent]);

    // Pagination
    useEffect(() => {
        getItems(filters, sortData, skip, limit, false);
    }, [skip, limit, filters, sortData, getItems]);

    // Search update
    useEffect(() => {
        if (initRef.current) {
            setFilters(
                !dbGlobalFilterValue
                    ? []
                    : [
                          {
                              field: 'programName',
                              value: dbGlobalFilterValue
                          }
                      ]
            );
        }
    }, [dbGlobalFilterValue]);

    // Init
    useEffect(() => {
        initRef.current = true;
    }, []);

    return (
        <Card title="Program Locations Dashboard" className="pageCard">
            <div className="grid">
                <div className="col">
                    <Button
                        icon="pi pi-plus-circle"
                        rounded
                        severity="success"
                        onClick={() => {
                            setOpenCreate(true);
                        }}
                    />

                    <Button
                        className="ml-1"
                        icon="pi pi-refresh"
                        rounded
                        severity="help"
                        onClick={() => {
                            getItems(filters, sortData, skip, limit, false);
                        }}
                    />
                </div>

                <div className="col">
                    <span className="p-input-icon-left" style={{ float: 'right' }}>
                        <i className="pi pi-search" />
                        <InputText
                            style={{ minWidth: '333px' }}
                            value={globalFilterValue}
                            onChange={(e) => {
                                setGlobalFilterValue(e.target.value);
                            }}
                            placeholder="Name Search"
                        />
                        <span className="ml-2">
                            <Button
                                icon="pi pi-times"
                                size="small"
                                rounded
                                severity="danger"
                                onClick={() => {
                                    setGlobalFilterValue('');
                                }}
                            />
                        </span>
                    </span>
                </div>
            </div>

            <DataTable
                value={programs}
                emptyMessage="No program locations found"
                showGridlines
                removableSort
                sortField={sortField}
                sortOrder={sortOrder}
                loading={loading}
                rowClassName={(e) => {
                    return e.active ? '' : 'rowInactive';
                }}
                onSort={(event) => {
                    const sortData = {};

                    sortData[event.sortField] = event.sortOrder;

                    setSortField(event.sortField);
                    setSortOrder(event.sortOrder);

                    if (event.sortField) {
                        setSortData({ ...sortData });
                    } else {
                        setSortData({});
                    }
                }}
            >
                <Column field="programName" header="Program Name" sortable></Column>
                <Column field="programType.itemName" header="Program Type" sortable></Column>
                <Column field="contactNameFirst" header="First Name" sortable></Column>
                <Column field="contactNameLast" header="Last Name" sortable></Column>
                <Column field="contactTitle" header="Title" sortable></Column>
                <Column body={EmailDisplay} header="Email"></Column>
                <Column header="Address" body={AddressDisplay}></Column>
                <Column header="Notes" body={NotesDisplay}></Column>
                <Column
                    header="Actions"
                    bodyStyle={{ width: '150px' }}
                    body={ActionsDisplay}
                ></Column>
            </DataTable>

            <Paginator
                alwaysShow={true}
                leftContent={
                    <PLeft
                        filteredRecordCount={filteredRecordCount}
                        recordCount={recordCount}
                        skip={skip}
                        limit={limit}
                    />
                }
                first={skip}
                rows={limit}
                totalRecords={filters.length > 0 ? filteredRecordCount : recordCount}
                rowsPerPageOptions={[5, 10, 20, 30]}
                onPageChange={(pageEvent) => {
                    setSkip(pageEvent.first);
                    setLimit(pageEvent.rows);
                }}
            />

            {!openCreate ? null : (
                <ProgramCreate
                    open={openCreate}
                    onClose={(res) => {
                        if (res.status === 200) {
                            getItems(filters, sortData, skip, limit, false);
                        }

                        setOpenCreate(false);
                    }}
                />
            )}

            {!openUpdate ? null : (
                <ProgramUpdate
                    open={openUpdate}
                    data={updateData}
                    onClose={(res) => {
                        if (res.status === 200) {
                            getItems(filters, sortData, skip, limit, false);
                        }

                        setUpdateData({});
                        setOpenUpdate(false);
                    }}
                />
            )}

            <Toast ref={toast} />
        </Card>
    );
}

export default ProgramsPage;
